/**
 * Pick the base direction of a piece of user- or agent-written text from its
 * content, independent of the UI locale.
 *
 * The browser's own heuristics (`dir="auto"`, `unicode-bidi: plaintext`, and
 * Lexical's per-paragraph `dir="auto"`) all use the FIRST strong character.
 * That is exactly what misreads the commonest mixed sentence an Arabic user
 * writes — an Arabic sentence that opens with a brand or product name
 * ("Soku هو الأفضل.") resolves left-to-right and its full stop jumps to the
 * wrong end. Counting letters instead lets the language that carries the
 * sentence decide; the first strong character only breaks an exact tie.
 *
 * URLs and email addresses are dropped before counting: they are Latin
 * whatever language surrounds them and would otherwise outvote a short
 * Arabic sentence that cites a link.
 */

export type TextDirection = 'ltr' | 'rtl'

// Hebrew, Arabic, Syriac, Arabic Supplement, Thaana, NKo, Samaritan, Mandaic,
// Syriac Supplement, Arabic Extended-A/B, plus the Hebrew / Arabic
// presentation-form blocks.
const RTL_LETTER = /[֐-ࣿיִ-﷿ﹰ-ﻼ]/
const LETTER = /\p{L}/u
// A link needs at least one character after its prefix; a bare `https://` is text.
const URL_START = /\b(?:https?:\/\/|www\.)(?=\S)/i

/**
 * The text with each URL and email address replaced by a space, decided one
 * whitespace-delimited run at a time. A single `\S+@\S+\.\S+` pattern rescans
 * a run from every position it could start at, and a Chinese paragraph is
 * one run: a 263 KB conversation held the main thread for 40 s.
 */
function withoutLinks(text: string): string {
  return text.replace(/\S+/g, run => {
    // An address takes its whole run; a link, the run from where it starts.
    const at = run.indexOf('@', 1)
    if (at > 0 && run.lastIndexOf('.', run.length - 2) >= at + 2) return ' '
    const link = run.search(URL_START)
    return link < 0 ? run : `${run.slice(0, link)} `
  })
}

/** `null` when the text has no letters at all — inherit the surrounding direction. */
export function detectTextDirection(text: string): TextDirection | null {
  if (!text) return null
  let rtl = 0
  let ltr = 0
  let first: TextDirection | null = null
  for (const char of withoutLinks(text)) {
    if (!LETTER.test(char)) continue
    if (RTL_LETTER.test(char)) {
      rtl += 1
      first ??= 'rtl'
    } else {
      ltr += 1
      first ??= 'ltr'
    }
  }
  if (rtl === ltr) return first
  return rtl > ltr ? 'rtl' : 'ltr'
}

// ---------------------------------------------------------------------------
// Markdown: a remark plugin that stamps `dir` on each block.
// ---------------------------------------------------------------------------

type MdNode = {
  type: string
  value?: string
  children?: MdNode[]
  data?: { hProperties?: Record<string, unknown> } & Record<string, unknown>
}

// Blocks that get their own direction. Lists and tables are decided by their
// whole content so the bullet / column side matches the bulk of the rows;
// each item and paragraph inside still decides for itself.
const DIRECTED_BLOCKS = new Set([
  'paragraph',
  'heading',
  'blockquote',
  'list',
  'listItem',
  'table',
])

// Code is left-to-right by definition and must not vote.
const SKIPPED = new Set(['code', 'inlineCode', 'html', 'math', 'inlineMath'])

function collectText(node: MdNode): string {
  if (SKIPPED.has(node.type)) return ''
  if (typeof node.value === 'string') return node.value
  return (node.children ?? []).map(collectText).join(' ')
}

function stampDirections(node: MdNode): void {
  if (DIRECTED_BLOCKS.has(node.type)) {
    const dir = detectTextDirection(collectText(node))
    if (dir) {
      node.data = {
        ...node.data,
        hProperties: { ...node.data?.hProperties, dir },
      }
    }
  }
  for (const child of node.children ?? []) stampDirections(child)
}

/** remark plugin: `dir="ltr|rtl"` on paragraphs, headings, quotes, lists and tables. */
export function remarkBlockDirection() {
  return (tree: MdNode) => {
    stampDirections(tree)
  }
}

// ---------------------------------------------------------------------------
// DOM helpers shared by the global field listener and the Lexical editors.
// ---------------------------------------------------------------------------

/** Set (or clear, when there is nothing to decide on) an element's `dir`. */
export function applyDirection(el: HTMLElement, text: string): void {
  const dir = detectTextDirection(text)
  if (dir) {
    if (el.dir !== dir) el.dir = dir
  } else if (el.hasAttribute('dir')) {
    el.removeAttribute('dir')
  }
}
